<template>
  <div class="section-page">
    <!-- Information Card (Visible only for specific subcategories) -->
    <InformationCard v-if="['property-for-sale', 'property-for-rent'].includes(subcategoryKey)" />

    <!-- Title Section -->
    <CategoryTitle> You are almost there! </CategoryTitle>

    <!-- Subheading -->
    <Subheading :title="subheadingText" />

    <!-- Breadcrumbs -->
    <Breadcrumbs />

    <!-- Form Section -->
    <!-- Form Section -->
    <v-container class="form-container">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-row dense>
          <!-- Dynamically Render Fields -->
          <v-col v-for="field in fields" :key="field.name" cols="12">
            <!-- Price + Currency -->
            <!-- Price + Currency (Enhanced) -->
            <template v-if="field.name === 'price'">
              <v-row>
                <v-col cols="8">
                  <v-text-field
                    v-model="formData.price"
                    :label="field.label"
                    :rules="getDynamicFieldRules(field)"
                    variant="outlined"
                    dense
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter price"
                  />
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="formData.currency"
                    :items="currencies"
                    label="Currency *"
                    :rules="[requiredRule]"
                    variant="outlined"
                    dense
                  />
                </v-col>
              </v-row>
            </template>

            <!-- Phone Number (Enhanced) -->
            <template v-else-if="field.name === 'phone' || field.name === 'phoneNumber'">
              <v-text-field
                v-model="formData[field.name]"
                :label="field.label"
                :placeholder="field.placeholder || '+252612345678'"
                :rules="getDynamicFieldRules(field)"
                variant="outlined"
                dense
              >
                <template #prepend-inner>
                  <v-icon size="20" color="grey">mdi-phone</v-icon>
                </template>
              </v-text-field>
            </template>

            <!-- Images (Enhanced with better error display) -->
            <template
              v-else-if="(field.type === 'file' || field.name === 'images') && field.name !== 'cv'"
            >
              <div class="image-upload-section">
                <v-file-input
                  v-model="formData[field.name]"
                  :label="field.label"
                  prepend-icon="mdi-camera"
                  multiple
                  show-size
                  accept="image/*"
                  :rules="getDynamicFieldRules(field)"
                  variant="outlined"
                  counter
                  @change="onImagesChange"
                  :error="!!imagesError"
                  :error-messages="imagesError"
                >
                  <template #selection>
                    <div v-if="formData[field.name] && formData[field.name].length">
                      <v-chip
                        v-for="(file, i) in formData[field.name]"
                        :key="i"
                        class="ma-1"
                        color="primary"
                        label
                        small
                        closable
                        @click:close="removeImage(i)"
                      >
                        {{ file.name }}
                      </v-chip>
                    </div>
                    <span v-else class="grey--text">No images selected</span>
                  </template>
                </v-file-input>

                <!-- Helper text -->
                <div class="upload-helper-text">
                  <v-icon size="16" color="grey" class="mr-1">mdi-information</v-icon>
                  <span class="text-caption text-grey">
                    Upload up to 10 images, max 5MB each. Supported: JPG, PNG, GIF
                  </span>
                </div>
              </div>
            </template>

            <!-- Heavy Vehicle Make Dropdown -->
            <template v-else-if="field.dynamic === 'heavyVehicleMake'">
              <v-select
                v-model="formData.make"
                :items="makes"
                item-title="name"
                item-value="slug"
                label="Make *"
                :rules="[requiredRule]"
                dense
                return-object
                class="custom-field"
                @update:modelValue="onMakeChange"
                placeholder="Select vehicle make"
              ></v-select>
            </template>

            <!-- Heavy Vehicle Model Dropdown (appears when make is selected) -->
            <template
              v-else-if="
                field.dynamic === 'heavyVehicleModel' && formData.make && models.length > 0
              "
            >
              <v-select
                v-model="formData.model"
                :items="models"
                item-title="name"
                item-value="slug"
                label="Model *"
                :rules="[requiredRule]"
                dense
                return-object
                class="custom-field"
                placeholder="Select vehicle model"
              ></v-select>
            </template>

            <!-- Loading indicator for models -->
            <template v-else-if="field.dynamic === 'heavyVehicleModel' && loading">
              <div class="text-center">
                <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                <span class="ml-2">Loading models...</span>
              </div>
            </template>

            <!-- Error message for vehicle data -->
            <template v-else-if="field.dynamic === 'heavyVehicleModel' && error">
              <v-alert type="error" dense>{{ error }}</v-alert>
            </template>

            <!-- Plate Code Dropdown -->
            <template v-else-if="field.component === 'PlateCodeDropdown'">
              <div class="plate-code-wrapper">
                <PlateCodeDropdown
                  v-model="formData.plateCode"
                  @update:modelValue="
                    (value) => {
                      console.log('Parent received plate code:', value);
                      formData.plateCode = value;
                    }
                  "
                />
              </div>
            </template>

            <!-- Design Toggle -->
            <template v-else-if="field.component === 'DesignToggle'">
              <DesignToggle
                v-model="formData[field.name]"
                @update:modelValue="(value) => (formData[field.name] = value)"
              />
            </template>

            <!-- CV Upload with JobApplicationDrawer Style -->
            <!-- CV Upload with Custom Design -->
            <!-- CV Upload (Enhanced) -->
            <template v-else-if="field.name === 'cv'">
              <div class="cv-upload-container">
                <label class="cv-upload-label">{{ field.label }}</label>
                <div
                  class="cv-upload-area"
                  :class="{ 'cv-upload-error': cvError }"
                  @click="triggerFileUpload"
                  @dragover.prevent
                  @dragenter.prevent
                  @drop.prevent="handleCvDrop"
                >
                  <div class="cv-upload-content">
                    <div class="cv-upload-icon">
                      <v-icon size="32" color="#4285F4">mdi-upload</v-icon>
                    </div>
                    <div class="cv-upload-text">
                      <span class="cv-upload-main-text">Drag and drop or click to select</span>
                    </div>
                    <div class="cv-upload-subtext">
                      File should be PDF, DOC, DOCX, or image files below 5MB
                    </div>
                  </div>

                  <!-- Selected files display -->
                  <div v-if="formData.cv && formData.cv.length" class="cv-selected-files">
                    <div v-for="(file, i) in formData.cv" :key="i" class="cv-file-chip">
                      <v-icon size="16" class="mr-1">
                        {{ getFileIcon(file.type) }}
                      </v-icon>
                      {{ file.name }}
                      <v-btn
                        icon
                        size="x-small"
                        variant="text"
                        @click.stop="removeCvFile(i)"
                        class="ml-1"
                      >
                        <v-icon size="14">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>

                <!-- Error message -->
                <div v-if="cvError" class="cv-error-message">
                  <v-icon size="16" color="error" class="mr-1">mdi-alert-circle</v-icon>
                  {{ cvError }}
                </div>

                <!-- Helper text -->
                <div v-else class="cv-helper-text">
                  <v-icon size="16" color="grey" class="mr-1">mdi-information</v-icon>
                  <span class="text-caption text-grey">
                    Upload up to 5 files. Supported: PDF, DOC, DOCX, JPG, PNG
                  </span>
                </div>

                <!-- Hidden file input -->
                <input
                  ref="fileInput"
                  type="file"
                  class="cv-hidden-input"
                  @change="onCvChange"
                  :accept="field.accept || '.pdf,.doc,.docx,.jpg,.jpeg,.png'"
                  multiple
                />
              </div>
            </template>

            <!-- Default text/select/textarea (Enhanced) -->
            <template
              v-else-if="!['cv', 'price', 'phone', 'phoneNumber', 'images'].includes(field.name)"
            >
              <v-text-field
                v-if="field.type === 'text'"
                v-model="formData[field.name]"
                :label="field.label"
                :placeholder="field.placeholder"
                :rules="getDynamicFieldRules(field)"
                variant="outlined"
                dense
              />

              <v-select
                v-else-if="field.type === 'selectIndustry'"
                v-model="formData[field.name]"
                :items="field.options"
                :label="field.label"
                :rules="field.required ? [requiredRule] : []"
                item-title="text"
                item-value="value"
                variant="outlined"
                dense
              />
              <v-select
                v-else-if="field.type === 'selectCompanySize'"
                v-model="formData[field.name]"
                :items="field.options"
                :label="field.label"
                :rules="field.required ? [requiredRule] : []"
                item-title="text"
                item-value="value"
                variant="outlined"
                dense
              />

              <v-select
                v-else-if="field.type === 'select'"
                v-model="formData[field.name]"
                :items="field.options"
                :label="field.label"
                :placeholder="field.placeholder"
                :rules="getDynamicFieldRules(field)"
                :clearable="!field.required"
                :searchable="field.searchable"
                variant="outlined"
                dense
                item-title="title"
                item-value="value"
              >
                <!-- Custom selection display for nationality -->
                <template v-if="field.name === 'nationality'" #selection="{ item }">
                  {{ item.title.replace(' ⭐', '') }}
                </template>
              </v-select>

              <v-textarea
                v-else-if="field.type === 'textarea'"
                v-model="formData[field.name]"
                :label="field.label"
                :placeholder="field.placeholder"
                :rules="getDynamicFieldRules(field)"
                variant="outlined"
                dense
                :counter="getTextareaCounter(field)"
              />
            </template>
          </v-col>
        </v-row>

        <!-- ... rest of existing components ... -->

        <!-- Learn More 360 Component -->
        <LearnMore360 />

        <!-- Additional Features -->
        <!-- Amenities/Features Checkbox (renamed to amenities) -->
        <div class="amenities-section">
          <CheckBoxFeatures
            v-model="formData.amenities"
            :category-slug="subcategoryKey"
            @update:modelValue="handleAmenitiesUpdate"
          />
        </div>

        <!-- Compatibilities Section -->
        <CompatibilitiesCheckBox
          v-model="formData.compatibilities"
          :group-slug="groupSlug"
          v-if="shouldShowCompatibilities"
          @update:modelValue="handleCompatibilitiesUpdate"
        />

        <!-- Landlord or Agent Section -->
        <!-- <LandlordOrAgent /> -->

        <!-- Google Maps Section -->
        <GoogleMapComponent
          ref="mapComponent"
          :cities="cities"
          :required="true"
          :height="'500px'"
          @locationSelected="handleLocationSelected"
          @locationCleared="handleLocationCleared"
          @error="handleMapError"
        />
        <div v-if="locationError" class="error--text mt-2">
          {{ locationError }}
        </div>

        <!-- Disclaimer Section -->
        <DisclaimerCard />

        <!-- Submit Button -->
        <!-- <div class="sticky-footer-container">
        <v-btn
          class="submit-button"
          :disabled="!valid"
          @click="submitForm"
        >
          Next
        </v-btn>
        </div>
      </v-form>
    </v-container> -->
        <!-- !-- Submit Button with Enhanced States -->
        <div class="sticky-footer-container">
          <v-btn
            class="submit-button"
            :disabled="!valid || isSubmitting"
            :loading="isSubmitting"
            @click="submitForm"
          >
            <v-icon v-if="!isSubmitting" class="mr-2"></v-icon>
            {{ isSubmitting ? 'Submitting...' : 'Next' }}
          </v-btn>
        </div>
      </v-form>
    </v-container>

    <!-- Loading Spinner -->
    <LoadingSpinner :active="isSubmitting" message="Submitting your listing..." />
  </div>
</template>

<script setup>
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import CategoryTitle from '@/components/CategoryTitle.vue';
import DisclaimerCard from '@/components/DisclaimerCard.vue';
import CheckBoxFeatures from '@/components/GeneralListingSignupChildComponents/CheckBoxFeatures.vue';
import GoogleMapComponent from '@/components/GeneralListingSignupChildComponents/GoogleMapComponent.vue';
import InformationCard from '@/components/GeneralListingSignupChildComponents/InformationCard.vue';
import LearnMore360 from '@/components/GeneralListingSignupChildComponents/LearnMore360.vue';
import Subheading from '@/components/Subheading.vue';
import { getFieldsByCategory, sanitizeInput } from '@/utils/fieldData';
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
//import LandlordOrAgent from "@/components/GeneralListingSignupChildComponents/LandlordOrAgent.vue";
import CompatibilitiesCheckBox from '@/components/GeneralListingSignupChildComponents/CompatibilitiesCheckBox.vue';
import DesignToggle from '@/components/GeneralListingSignupChildComponents/DesignToggle.vue';
import PlateCodeDropdown from '@/components/GeneralListingSignupChildComponents/PlateCodeDropdown.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import { useWhiteTheme } from '@/composables/useWhiteTheme'; // <-- ADD THIS
import ListingService from '@/services/ListingServicePostingEndpoints';
import CompanySizeService from '@/services/companySize.service';
import IndustryService from '@/services/industry.service';
import { isPropertyCategory } from '@/utils/categorySubCategorySlugUtils';
import { useVehicleData } from '@/utils/useVehicleData';

const store = useStore();
const router = useRouter();
const route = useRoute();

console.log('▶ Fire it up store.state:', store.state);

const valid = ref(false);
const imagesError = ref('');
const isSubmitting = ref(false); // Tracks the spinner state
const mapComponent = ref(null);
const locationError = ref('');
const currencies = ['USD', 'KES', 'SOS', 'TZS', 'UGX'];
const { makes, models, loading, error, fetchModelsForMake } = useVehicleData();

const personalProfile = computed(() => store.getters["personalProfile/profile"]);
const companyProfile = computed(
  () => store.getters["companyProfile/companyProfile"] // or 'company' if you rename it
);


// BETTER APPROACH (for clarity):
const formData = ref({
  userId: store.state.auth?.user?.id || null,
  currency: 'USD',
  amenities: [],
  compatibilities: [],
  make: null,
  model: null,
  cv: [],
});
const fileInput = ref(null);
const cvError = ref('');

const industryOptions = ref([]);
const companySizeOptions = ref([]);

const LanguageCheckboxesAsync = defineAsyncComponent(() =>
  import('@/components/Jobs/LanguageCheckboxes.vue')
);
const BenefitsCheckboxesAsync = defineAsyncComponent(() =>
  import('@/components/Jobs/BenefitsCheckboxes.vue')
);

const slugify = (text) =>
  text
    ?.toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-') || '';

const categoryId = computed(() => {
  const categorySlug = route.params.categorySlug;
  if (!categorySlug) return store.getters['NewCategory/selectedCategory']?.id || null;

  const category = store.state.NewCategory.categories.find((c) => c.slug === categorySlug);
  return category?.id || store.getters['NewCategory/selectedCategory']?.id || null;
});

const subcategoryId = computed(() => {
  const subcategorySlug = route.params.subcategorySlug;
  if (!subcategorySlug) return store.getters['NewCategory/selectedSubCategory']?.id || null;

  const subcategory = store.state.NewCategory.subcategories.find(
    (s) => s.slug === subcategorySlug || slugify(s.title) === subcategorySlug
  );
  return subcategory?.id || store.getters['NewCategory/selectedSubCategory']?.id || null;
});

const groupId = computed(() => {
  const groupSlug = route.params.groupSlug;
  if (!groupSlug) return store.getters['NewCategory/selectedGroup']?.id || null;

  const group = store.state.NewCategory.groups.find(
    (g) => g.slug === groupSlug || slugify(g.title) === groupSlug
  );
  return group?.id || store.getters['NewCategory/selectedGroup']?.id || null;
});

const sectionId = computed(() => {
  const sectionSlug = route.params.sectionSlug;
  if (!sectionSlug) return store.getters['NewCategory/selectedSection']?.id || null;

  const selectedGroup = store.state.NewCategory.groups.find((g) => g.id === groupId.value);
  if (selectedGroup?.sections) {
    const section = selectedGroup.sections.find(
      (s) => s.slug === sectionSlug || slugify(s.title) === sectionSlug
    );
    return section?.id || store.getters['NewCategory/selectedSection']?.id || null;
  }

  return store.getters['NewCategory/selectedSection']?.id || null;
});

// Retrieve IDs from Vuex (fallback if not in route)
const selectedCategory = computed(() => store.getters['NewCategory/selectedCategory']);
const selectedSubCategory = computed(() => store.getters['NewCategory/selectedSubCategory']);
const selectedGroup = computed(() => store.getters['NewCategory/selectedGroup']);
const selectedSection = computed(() => store.getters['NewCategory/selectedSection'] || null);
const cities = computed(() => store.state.NewCategory.cities);

// Dynamically determine the subcategoryKey
const subcategoryKey = computed(() => {
  const categorySlug = selectedCategory.value?.slug || route.params.categorySlug || null;
  // const subcategoryId = selectedSubCategory.value?.id || route.params.subcategoryId || null;

  // Combine categorySlug and subcategoryId for a unique key
  return `${categorySlug}` ? `${categorySlug}` : null;
});

// Fetch the selected subcategory and group from the route
const subcategoryName = computed(() => route.query.subcategoryName || 'Subcategory');
const groupName = computed(() => route.query.groupName || 'Group');

// Dynamically fetch fields based on category, subcategory, group, and section
// const fields = computed(() => {
//   return getFieldsByCategory(
//     store,
//     categoryId.value,
//     subcategoryId.value,
//     groupId.value,
//     sectionId.value
//   );
// });

const fields = computed(() => {
  // get the raw field definitions (with a `.dynamic` flag on industry & companySize)
  const raw = getFieldsByCategory(
    categoryId.value,
    subcategoryId.value,
    groupId.value,
    sectionId.value,
    route.params.subcategorySlug || ''
  );

  // now map → produce a fresh array with correct .options
  return raw.map((field) => {
    if (field.dynamic === 'industry') {
      return {
        ...field,
        options: industryOptions.value.map((i) => ({
          text: i.name,
          value: i.id,
        })),
      };
    }
    if (field.dynamic === 'companySize') {
      return {
        ...field,
        options: companySizeOptions.value.map((c) => ({
          text: c.range,
          value: c.id,
        })),
      };
    }
    return field;
  });
});

// Amenities handler (was features)
const handleAmenitiesUpdate = (val) => {
  formData.value.amenities = val;
};

// Computed property for subheading text
const subheadingText = computed(() => {
  if (selectedCategory.value?.slug === 'property-for-sale') {
    return 'Include as much details and pictures as possible, and set the right price!';
  } else if (selectedCategory.value?.slug === 'motors') {
    return 'Include as much details and pictures as possible!';
  }
  return `${subcategoryName.value} > ${groupName.value}`;
});

const handleLocationSelected = (location) => {
  console.log('Location selected:', location);
  // Update form data with location details
  formData.value.selectedLat = location.lat;
  formData.value.selectedLng = location.lng;
  formData.value.locationName = location.name;
  formData.value.cityId = location.id;
  formData.value.cityName = location.name;
};

const handleLocationCleared = () => {
  console.log('Location cleared');
  // Clear location data from form
  delete formData.value.selectedLat;
  delete formData.value.selectedLng;
  delete formData.value.locationName;
  delete formData.value.cityId;
  delete formData.value.cityName;
};

const handleMapError = (error) => {
  console.error('Map error:', error);
  // Handle map errors (show notification, etc.)
};

const handleCompatibilitiesUpdate = (selectedCompatibilities) => {
  console.log('🎯 PARENT: Updated compatibilities:', selectedCompatibilities);
  formData.value.compatibilities = selectedCompatibilities;
};

// Validation rules
//Start here for the validations that have changed
// Enhanced validation rules with user-friendly messages
const requiredRule = (value) => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return 'This field is required.';
  }
  return true;
};

const numberRule = (value) => {
  if (value === null || value === '' || value === undefined) return true;
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return 'Please enter a valid number.';
  }
  return true;
};

const positivePriceRule = (value) => {
  if (!value) return true;
  const numValue = parseFloat(value);
  if (numValue <= 0) {
    return 'Price must be greater than zero.';
  }
  return true;
};

const phoneNumberRule = (value) => {
  if (!value) return true; // Only validate if value exists
  const regex = /^\+(252|254|255|256|257|211|250|251)[0-9]{7,12}$/;
  if (!regex.test(value)) {
    return 'Please enter a valid East African phone number (e.g., +252612345678).';
  }
  return true;
};

// Enhanced dynamic field validation with user-friendly messages
const getDynamicFieldRules = (field) => {
  const rules = [];

  // Add required validation first
  if (field.required) {
    rules.push(requiredRule);
  }

  // Add field-specific validation from fieldData.js
  if (field.validation && typeof field.validation === 'function') {
    const specificRules = field.validation(field);
    rules.push(...specificRules);
  } else {
    // Fallback to basic type-based validation
    switch (field.type) {
      case 'text':
        if (['price', 'pricePerYear', 'kilometers', 'kilometres'].includes(field.name)) {
          rules.push(numberRule);
          if (field.name.includes('price')) {
            rules.push(positivePriceRule);
          }
        }
        if (['phoneNumber', 'phone'].includes(field.name)) {
          rules.push(phoneNumberRule);
        }
        break;
      case 'textarea':
        // Add textarea-specific rules if needed
        break;
      case 'select':
        // Select validation is handled by the component itself
        break;
    }
  }

  return rules;
};

// Enhanced input sanitization handler
const handleFieldInput = (field, value) => {
  if (field.type === 'text' || field.type === 'textarea') {
    const sanitizedValue = sanitizeInput(value);
    formData.value[field.name] = sanitizedValue;

    // Show warning if content was sanitized
    if (sanitizedValue !== value) {
      console.warn(`Input for ${field.name} was sanitized for security reasons.`);
    }
  } else {
    formData.value[field.name] = value;
  }
};

// Enhanced images validation with better error messages
const imagesRule = (files) => {
  if (!files || !files.length) {
    return 'Please select at least one image.';
  }

  if (files.length > 10) {
    return 'You can upload up to 10 images. Please remove some images.';
  }

  for (const file of files) {
    if (!file || typeof file !== 'object') {
      return 'One or more files are invalid. Please try again.';
    }

    if (!file.type || typeof file.type !== 'string' || !file.type.startsWith('image/')) {
      return `"${file.name}" is not a valid image file. Please select only image files.`;
    }

    if (file.size && file.size > 5 * 1024 * 1024) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return `"${file.name}" is ${sizeMB}MB. Please select images smaller than 5MB.`;
    }
  }

  return true;
};

const descriptionRule = (value) => {
  if (!value) return 'Description is required.';
  const wordCount = value.trim().split(/\s+/).length;
  if (wordCount > 16000) return 'Description cannot exceed 16,000 words.';
  const invalidCharactersRegex = /^[a-zA-Z0-9\s.,'"\-!?()]+$/; // Allow spaces
  return invalidCharactersRegex.test(value) ? true : 'Description contains invalid characters.';
};

// Specific rule for job CV summary
const cvSummaryRule = (value) => {
  if (!value || !value.trim()) return true;

  const words = value
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  if (words.length > 500) {
    return `CV Summary cannot exceed 500 words. Current: ${words.length} words.`;
  }

  if (words.length < 10) {
    return 'CV Summary should contain at least 10 words.';
  }

  return true;
};

// Property description rule
const propertyDescriptionRule = (value) => {
  if (!value || !value.trim()) return true;

  const words = value
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  if (words.length > 1000) {
    return `Property description cannot exceed 1,000 words. Current: ${words.length} words.`;
  }

  if (words.length < 5) {
    return 'Property description should contain at least 5 words.';
  }

  return true;
};

// Add this function to determine validation rules:

const getTextareaRules = (field) => {
  const rules = [];

  // Add required rule if field is required
  if (field.required) {
    rules.push(requiredRule);
  }

  // Add field-specific validation rules
  switch (field.name) {
    case 'cvSummary':
      rules.push(cvSummaryRule);
      break;
    case 'description':
      // Check if it's a property description
      if (isPropertyCategory(subcategoryKey.value)) {
        rules.push(propertyDescriptionRule);
      } else {
        rules.push(descriptionRule);
      }
      break;
    case 'job_description':
      rules.push(cvSummaryRule); // Reuse CV summary rule for job descriptions
      break;
    default:
      rules.push(descriptionRule);
      break;
  }

  return rules;
};

const onImagesChange = (files) => {
  console.log('Images changed:', files);

  // Clear any previous error
  imagesError.value = '';

  // If files exist and it's an array, validate them
  if (files && Array.isArray(files) && files.length > 0) {
    const validationResult = imagesRule(files);
    if (validationResult !== true) {
      imagesError.value = validationResult;
    }
  }
};

// Enhanced CV validation with better error messages
const cvRule = (files) => {
  if (!files || !files.length) {
    return 'Please upload your CV.';
  }

  if (files.length > 5) {
    return 'You can upload up to 5 CV files. Please remove some files.';
  }

  const validTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const typeNames = {
    'application/pdf': 'PDF',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  };

  for (const file of files) {
    if (!file || typeof file !== 'object') {
      return 'One or more files are invalid. Please try again.';
    }

    if (!file.type || !validTypes.includes(file.type)) {
      return `"${file.name}" is not supported. Please upload PDF, DOC, DOCX, or image files only.`;
    }

    if (file.size && file.size > 5 * 1024 * 1024) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return `"${file.name}" is ${sizeMB}MB. Please select files smaller than 5MB.`;
    }
  }

  return true;
};
869;

// Add CV file upload methods
const triggerFileUpload = () => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};
// Add these new methods for CV handling
const handleCvDrop = (event) => {
  const files = Array.from(event.dataTransfer.files);
  processCvFiles(files);
};

const processCvFiles = (files) => {
  if (!files.length) {
    formData.value.cv = [];
    cvError.value = '';
    return;
  }

  // Validate files
  const validationResult = cvRule(files);
  if (validationResult !== true) {
    cvError.value = validationResult;
    return;
  }

  formData.value.cv = files;
  cvError.value = '';
  console.log(
    'CV files processed:',
    files.map((f) => f.name)
  );
};

const removeCvFile = (index) => {
  const currentFiles = [...formData.value.cv];
  currentFiles.splice(index, 1);
  formData.value.cv = currentFiles;

  if (currentFiles.length === 0) {
    cvError.value = '';
  }
};
// Update the existing onCvChange method
const onCvChange = (event) => {
  const files = Array.from(event.target.files || []);
  processCvFiles(files);
  // Clear the input to allow re-selection of the same file
  event.target.value = '';
};

// Helper method to get appropriate file icon
const getFileIcon = (fileType) => {
  if (fileType.includes('pdf')) return 'mdi-file-pdf-box';
  if (fileType.includes('word') || fileType.includes('document')) return 'mdi-file-word-box';
  if (fileType.includes('image')) return 'mdi-file-image';
  return 'mdi-file-document';
};

// Helper method to determine textarea counter
const getTextareaCounter = (field) => {
  switch (field.name) {
    case 'cvSummary':
      return 2500; // ~500 words
    case 'job_description':
      return 4000; // ~800 words
    case 'description':
      return 5000; // ~1000 words
    default:
      return 16000;
  }
};

// Enhanced image removal
const removeImage = (index) => {
  const currentImages = [...(formData.value.images || [])];
  currentImages.splice(index, 1);
  formData.value.images = currentImages;

  // Clear error if images are now valid
  if (currentImages.length > 0) {
    const validation = imagesRule(currentImages);
    if (validation === true) {
      imagesError.value = '';
    }
  }
};

watch(
  () => formData.value,
  (newFormData) => {
    fields.value.forEach((field) => {
      // Skip sanitization for textarea fields - let users type naturally
      if (field.type === 'textarea') {
        return; // Skip textareas completely
      }

      // Only sanitize text fields (not textareas)
      if (field.type === 'text' && newFormData[field.name]) {
        const currentValue = newFormData[field.name];
        const sanitizedValue = sanitizeInput(currentValue);

        if (sanitizedValue !== currentValue) {
          console.warn(`Auto-sanitized ${field.name}`);
          formData.value[field.name] = sanitizedValue;
        }
      }
    });
  },
  { deep: true }
);

// Update the preparePayload function:
const preparePayload = (formData) => {
  const userId = store.state.auth?.user?.id || formData.userId || null;
  const city = store.state.NewCategory.selectedCity || {};

  const cityId = city.id || formData.cityId || null;
  const cityName = city.name || formData.cityName || null;
  const selectedLat = formData.selectedLat ?? city.lat ?? null;
  const selectedLng = formData.selectedLng ?? city.lng ?? null;

  // Start with all fields from formData
  const payload = { ...formData };

  // Overwrite/add system fields
  payload.userId = userId;
  payload.cityId = cityId;
  payload.cityName = cityName;
  payload.selectedLat = selectedLat;
  payload.selectedLng = selectedLng;

  // Handle make and model for heavy vehicles
  if (formData.make && typeof formData.make === 'object') {
    payload.make = formData.make.name || formData.make;
  }
  if (formData.model && typeof formData.model === 'object') {
    payload.model = formData.model.name || formData.model;
  }

  // Keep amenities as full objects with is_available property
  // Ensure amenities have the correct structure for backend
  if (Array.isArray(payload.amenities)) {
    payload.amenities = payload.amenities.map((amenity) => ({
      id: amenity.id,
      name: amenity.name || '',
      icon: amenity.icon || null,
      is_available: amenity.is_available !== undefined ? amenity.is_available : true,
    }));
  } else {
    payload.amenities = [];
  }

  // Include compatibilities in the correct format for backend
  if (Array.isArray(payload.compatibilities)) {
    payload.compatibilities = payload.compatibilities.map((compatibility) => ({
      id: compatibility.id,
      name: compatibility.name,
      is_compatible: compatibility.is_compatible !== undefined ? compatibility.is_compatible : true,
    }));
  } else {
    payload.compatibilities = [];
  }

  // Map Number Plates specific fields
  if (formData.plateCode) {
    payload.plate_code = formData.plateCode;
    console.log('Mapping plateCode to plate_code:', formData.plateCode);
  }
  if (formData.plateNumber) {
    payload.plate_number = formData.plateNumber;
    console.log('Mapping plateNumber to plate_number:', formData.plateNumber);
  }
  if (formData.designType) {
    payload.design_type = formData.designType;
    console.log('Mapping designType to design_type:', formData.designType);
  }

  // Normalize images
  payload.images = formData.images || formData.pictures || [];

  // Set currency default
  payload.currency = formData.currency || 'USD';

  // Normalize kilometers (handle both spellings)
  if (formData.kilometres !== undefined) {
    payload.kilometers = formData.kilometres;
  }
  // Append CV files
  const cvFiles = formData.cv || [];
  payload.cv = cvFiles.map((file) => (file instanceof File ? file : null));

  return payload;
};

const submitForm = async () => {
  // 1. First validate the form fields
  if (!valid.value) {
    console.error('Form validation failed');
    return;
  }

  // 2. Validate map location
  if (mapComponent.value) {
    const locationValidation = mapComponent.value.validateLocation();
    if (locationValidation !== true) {
      console.error('Location validation failed:', locationValidation);
      locationError.value = locationValidation;
      return;
    }
  }

  // Clear any previous error messages
  imagesError.value = '';
  cvError.value = '';
  locationError.value = '';

  // 3. Validate CV files if CV field exists and is required
  const cvField = fields.value.find((field) => field.name === 'cv');
  if (cvField?.required) {
    const cvFiles = formData.value.cv;
    if (!cvFiles || !cvFiles.length) {
      cvError.value = 'At least one CV file is required.';
      return;
    }

    const cvValidationResult = cvRule(cvFiles);
    if (cvValidationResult !== true) {
      cvError.value = cvValidationResult;
      return;
    }
  }

  // 4. Validate images if image field exists and is required
  const imageField = fields.value.find(
    (field) => (field.type === 'file' || field.name === 'images') && field.name !== 'cv'
  );
  if (imageField?.required) {
    const images = formData.value.images;
    if (!images || !images.length) {
      imagesError.value = 'At least one image is required.';
      return;
    }

    const imageValidationResult = imagesRule(images);
    if (imageValidationResult !== true) {
      imagesError.value = imageValidationResult;
      return;
    }
  }

  // 5. Prepare payload
  const payload = preparePayload({
    ...formData.value,
    personal_hiring_id: personalProfile.value?.id,
    company_hiring_id: companyProfile.value?.id,
    categoryId: categoryId.value || selectedCategory.value?.id,
    subCategoryId: subcategoryId.value || selectedSubCategory.value?.id,
    groupId: groupId.value || selectedGroup.value?.id,
    sectionId: sectionId.value?.id || null,
  });

  // 6. Convert payload to FormData
  const formDataToSend = new FormData();

  // Get images and CV files
  const images = formData.value.images || [];
  const cvFiles = formData.value.cv || [];

  // Append all fields except images and cv
  Object.entries(payload).forEach(([key, value]) => {
    if (key === 'images' || key === 'cv') return;
    if (key === 'amenities' && Array.isArray(value)) {
      formDataToSend.append(key, JSON.stringify(value));
    } else if (key === 'compatibilities' && Array.isArray(value)) {
      formDataToSend.append(key, JSON.stringify(value));
    } else if (value !== undefined && value !== null) {
      formDataToSend.append(key, value);
    }
  });

  // Append images (as files)
  for (const file of images) {
    if (file instanceof File) {
      formDataToSend.append('images[]', file);
      console.log('Appending image file:', file.name, file.type, file.size);
    }
  }

  // Append CV files (as files)
  for (const file of cvFiles) {
    if (file instanceof File) {
      formDataToSend.append('cv[]', file);
      console.log('Appending CV file:', file.name, file.type, file.size);
    }
  }

  console.log(
    'Images to upload:',
    images.map((f) => f.name)
  );
  console.log(
    'CV files to upload:',
    cvFiles.map((f) => f.name)
  );
  console.log('Selected Lat/Lng:', payload.selectedLat, payload.selectedLng);
  console.log('Amenities (stringified):', JSON.stringify(payload.amenities));
  console.log('%c📦 Final FormData ready to POST:', 'color: green;', formDataToSend);

  try {
    isSubmitting.value = true;

    const resolvedCategoryId = categoryId.value || selectedCategory.value?.id;
    const resolvedSubcategoryId = subcategoryId.value || selectedSubCategory.value?.id;

    if (!resolvedSubcategoryId) {
      console.error('❌ subcategoryId is missing. Cannot resolve endpoint.');
      return;
    }

    const response = await ListingService.store(
      formDataToSend,
      resolvedSubcategoryId,
      resolvedCategoryId
    );

    console.log('%c✅ Form submitted successfully:', 'color: limegreen;', response.data);
    router.push({ name: 'SuccessPage' });
  } catch (error) {
    console.error('%c❌ Error submitting form:', 'color: red;', error);

    if (error.response?.data?.message) {
      cvError.value = error.response.data.message;
    } else {
      cvError.value = 'Submission failed. Please try again.';
    }
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(async () => {
  try {
    const [industryRes, companySizeRes] = await Promise.all([
      IndustryService.index(),
      CompanySizeService.index(),
    ]);
    industryOptions.value = industryRes.data?.data || [];
    companySizeOptions.value = companySizeRes.data?.data || [];
  } catch (e) {
    console.error(e);
  }
  //loadPersistedFormData();
  // Set the white theme to pure white
  const { setWhiteShade } = useWhiteTheme();
  setWhiteShade('pure');
});
</script>

<style scoped>
/* General Page Styling */
.section-page {
  max-width: 50%;
  margin: 0 auto;
  padding: 16px;
  position: relative;
  transition: max-width 0.3s ease, padding 0.3s ease;
  background-color: var(--white-pure) !important;
}

/* Subheading Styling - Adjusted positioning and padding */
:deep(.subheading-container) {
  padding: 8px 16px 12px;
  margin-left: 8px;
  text-align: left;
}

/* Breadcrumbs Styling - Ensure proper fit */
:deep(.v-breadcrumbs) {
  padding: 8px 16px;
  width: 100%;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

:deep(.v-breadcrumbs)::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

:deep(.v-breadcrumbs-item) {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}

:deep(.v-breadcrumbs-item--active) {
  color: #b30000;
  font-weight: 600;
}

:deep(.v-breadcrumbs-divider) {
  padding: 0 8px;
  color: rgba(0, 0, 0, 0.4);
}

/* Form Container */
.form-container {
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  transition: padding 0.3s ease;
}

/* Form Card Styling */
.form-card {
  margin-bottom: 16px;
  transition: all 0.3s ease;
  box-shadow: none !important;
}

/* Text Fields, Select Fields, and File Inputs */
:deep(.v-text-field),
:deep(.v-select),
:deep(.v-textarea),
:deep(.v-file-input) {
  margin-bottom: 8px;
  border-radius: 4px;
}

:deep(.v-text-field .v-input__slot),
:deep(.v-select .v-input__slot),
:deep(.v-textarea .v-input__slot),
:deep(.v-file-input .v-input__slot) {
  min-height: 44px; /* Touch-friendly height */
  background-color: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.23) !important;
  transition: border-color 0.2s ease;
}

:deep(.v-text-field--outlined fieldset),
:deep(.v-select--outlined fieldset),
:deep(.v-textarea--outlined fieldset),
:deep(.v-file-input--outlined fieldset) {
  border-color: transparent !important;
}

:deep(.v-text-field .v-input__slot:hover),
:deep(.v-select .v-input__slot:hover),
:deep(.v-textarea .v-input__slot:hover),
:deep(.v-file-input .v-input__slot:hover) {
  border-color: rgba(0, 0, 0, 0.5) !important;
}

:deep(.v-text-field--is-focused .v-input__slot),
:deep(.v-select--is-focused .v-input__slot),
:deep(.v-textarea--is-focused .v-input__slot),
:deep(.v-file-input--is-focused .v-input__slot) {
  border-color: #b30000 !important;
  box-shadow: 0 0 0 1px #b30000;
}

:deep(.v-label) {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
  font-weight: 500;
}

:deep(.v-label--active) {
  color: #b30000 !important;
}

/* Input Field Focus Colors */
:deep(.v-input--is-focused .v-input__slot) {
  border-color: #b30000 !important;
}

/* Submit Button */
.submit-button {
  width: 100%;
  background-color: darkgoldenrod !important;
  color: white !important;
  font-weight: bold;
  margin-top: 24px;
  padding: 12px 0;
  border-radius: 4px;
  text-transform: none;
  letter-spacing: 0.5px;
  font-size: 16px;
  height: auto;
  line-height: 1;
  transition: background-color 0.3s ease;
}

.submit-button:hover:not(:disabled) {
  background-color: darkgoldenrod !important;
}

.submit-button:disabled {
  background-color: darkgoldenrod !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Error Text */
.error--text {
  color: #ff5252;
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 8px;
}

/* File Input Custom Styling */
:deep(.v-file-input .v-input__prepend-outer) {
  margin-top: 8px;
}

:deep(.v-file-input .v-chip) {
  height: 24px;
  font-size: 12px;
}

/* Counter Text */
:deep(.v-counter) {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

/* Responsive Breakpoints */
@media (max-width: 1280px) {
  .section-page {
    max-width: 65%; /* Slightly wider on smaller desktops */
  }
}

@media (max-width: 960px) {
  .section-page {
    max-width: 80%; /* Wider on tablets */
  }

  .form-container {
    padding: 12px;
  }

  :deep(.subheading-container) {
    padding: 8px 12px 10px;
    margin-left: 4px;
  }

  /* Adjust breadcrumbs for medium screens */
  :deep(.v-breadcrumbs-item) {
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .section-page {
    max-width: 90%; /* Almost full width on small tablets */
    padding: 12px;
  }

  :deep(.v-breadcrumbs-item) {
    font-size: 12px;
  }

  :deep(.v-breadcrumbs-divider) {
    padding: 0 4px;
  }
}

/* Mobile Adjustments */
@media (max-width: 600px) {
  .section-page {
    max-width: 100%; /* Full width on mobile */
    padding: 8px 12px; /* Added horizontal padding for better visual spacing */
  }

  .form-container {
    padding: 12px 10px; /* Increased horizontal padding */
    border-radius: 8px; /* Restored border radius */
    margin: 0 2px; /* Added slight margin from edges */
  }

  /* Subheading on mobile */
  :deep(.subheading-container) {
    padding: 8px 12px 10px;
    margin-left: 2px;
    font-size: 14px;
  }

  /* Breadcrumbs on mobile */
  :deep(.v-breadcrumbs) {
    padding: 6px 10px;
    margin: 0 2px 10px;
    background-color: rgba(249, 249, 249, 0.5);
    border-radius: 6px;
  }

  :deep(.v-breadcrumbs-item) {
    font-size: 11px;
    font-weight: 500;
  }

  :deep(.v-breadcrumbs-divider) {
    padding: 0 3px;
    opacity: 0.6;
  }

  /* More compact form on mobile */
  :deep(.v-text-field),
  :deep(.v-select),
  :deep(.v-textarea),
  :deep(.v-file-input) {
    margin-bottom: 6px;
  }

  /* Adjust label size on mobile */
  :deep(.v-label) {
    font-size: 13px;
  }

  /* Optimize button for mobile */
  .submit-button {
    border-radius: 4px; /* Maintained border radius */
    margin: 16px 0 0 0;
    height: 48px; /* Slightly taller for easier tapping */
  }

  /* Remove unnecessary margins */
  :deep(.v-input__slot) {
    margin-bottom: 0;
  }
}

/* Small Mobile Screens */
@media (max-width: 400px) {
  .form-container {
    padding: 10px 8px; /* Slightly increased from your original */
  }

  /* Price and currency layout adjustment */
  :deep(.v-col-8) {
    padding-right: 4px;
  }

  :deep(.v-col-4) {
    padding-left: 4px;
  }

  /* Ensure crisp text on small screens */
  :deep(.subheading-container) {
    font-size: 13px;
    line-height: 1.3;
  }
}

.sticky-footer-container {
  position: fixed;
  bottom: 0;
  left: 57.4%;
  transform: translateX(-50%);
  width: 38.5%; /* match your .section-page max-width */
  background: white; /* or whatever matches your page bg */
  padding: 12px 0;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  justify-content: center;
}

@media (max-width: 1780px) {
  .submit-button {
    width: 100%;
    background-color: darkgoldenrod !important;
    color: white !important;
    font-weight: bold;
    margin-top: 24px;
    padding: 10px 0;
    border-radius: 4px;
    text-transform: none;
    letter-spacing: 0.5px;
    font-size: 16px;
    height: auto;
    line-height: 1;
    transition: background-color 0.3s ease;
 
  }

  .sticky-footer-container {
    position: fixed;
    bottom: 0;
    left: 60.3%;
    transform: translateX(-50%);
    width: 33.6%; /* match your .section-page max-width */
    background: white; /* or whatever matches your page bg */
    padding: 12px 0;
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    display: flex;
    justify-content: center;
  }
}

@media (max-width: 600px) {
  .sticky-footer-container {
    width: calc(100% - 32px); /* 16px gutter each side */
    left: 16px;
    transform: none;
  }
}

/* Thin placeholder text for all form fields */
:deep(input::placeholder),
:deep(textarea::placeholder),
:deep(.v-input input::placeholder),
:deep(.v-text-field input::placeholder),
:deep(.v-textarea textarea::placeholder) {
  font-weight: 300 !important;
  color: #b0b0b0 !important;
  opacity: 1 !important;
}
</style>
